/**
 * OnboardingStatusBadge Component
 * Issue #1455 - Display onboarding status in admin dashboard
 *
 * Shows the current status of a fair manager's onboarding process:
 * - Not Started: Application approved but onboarding hasn't begun
 * - In Progress: Shows current step and percentage complete
 * - Completed: Shows completion date
 */

import React from 'react';
import { CheckCircle, Clock, PlayCircle } from 'lucide-react';

interface OnboardingStatus {
  status: 'not_started' | 'in_progress' | 'completed';
  current_step?: string;
  percentage?: number;
  completed_at?: string | null;
}

interface OnboardingStatusBadgeProps {
  status: OnboardingStatus;
  compact?: boolean;
}

const STEP_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  tos: 'Terms',
  profile: 'Profile',
  contact: 'Contact',
  payment: 'Payment',
  fair_review: 'Review',
  done: 'Complete',
};

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export default function OnboardingStatusBadge({ status, compact = false }: OnboardingStatusBadgeProps) {
  // Not Started - Application approved but onboarding not initiated
  if (status.status === 'not_started') {
    return (
      <div
        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-300"
        role="status"
        aria-label="Onboarding not started"
      >
        <Clock className="h-3 w-3 mr-1" />
        {compact ? 'Not Started' : 'Onboarding Not Started'}
      </div>
    );
  }

  // Completed - Onboarding finished
  if (status.status === 'completed') {
    return (
      <div className="space-y-1" role="status" aria-label="Onboarding completed">
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          {compact ? 'Complete' : 'Onboarding Complete'}
        </div>
        {!compact && status.completed_at && (
          <p className="text-xs text-gray-600 ml-1">
            Completed: {formatDate(status.completed_at)}
          </p>
        )}
      </div>
    );
  }

  // In Progress - Show current step and progress
  if (status.status === 'in_progress') {
    const currentStepLabel = status.current_step ? STEP_LABELS[status.current_step] || status.current_step : 'Unknown';
    const percentage = status.percentage || 0;

    return (
      <div className="space-y-1" role="status" aria-label={`Onboarding in progress: ${Math.round(percentage)}% complete`}>
        <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-300">
          <PlayCircle className="h-3 w-3 mr-1" />
          {compact ? `${Math.round(percentage)}%` : `In Progress (${Math.round(percentage)}%)`}
        </div>
        {!compact && (
          <div className="text-xs text-gray-600 ml-1">
            Current step: <span className="font-medium">{currentStepLabel}</span>
          </div>
        )}
        {!compact && (
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div
              className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${percentage}%` }}
              role="progressbar"
              aria-valuenow={percentage}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}
      </div>
    );
  }

  // Fallback for unknown status
  return (
    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
      Unknown Status
    </div>
  );
}
